import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { stagger, fadeUp, viewport } from "@/lib/motion";
import { Users, ShieldAlert, ArrowRight, Globe2 } from "lucide-react";

// Five clinical stages of liver disease progression. Anatomically accurate
// AI-rendered liver illustrations paired with a subtle severity tone dot.
const STAGES = [
  { name: "Healthy",      caption: "Fully functioning",    img: "/liver-stages/healthy.png",   dot: "#5B8265" },
  { name: "Fatty Liver",  caption: "MASLD · often silent", img: "/liver-stages/fatty.png",     dot: "#D3B26B" },
  { name: "Fibrosis",     caption: "Scarring begins",      img: "/liver-stages/fibrosis.png",  dot: "#C67B5C" },
  { name: "Cirrhosis",    caption: "Largely irreversible", img: "/liver-stages/cirrhosis.png", dot: "#A24A34" },
  { name: "Liver Cancer", caption: "End-stage",            img: "/liver-stages/cancer.png",    dot: "#4E2418" },
];

export const WhyLiver = () => {
  return (
    <section id="why" className="relative bg-sa-bg py-12 md:py-16" data-testid="why-liver-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-3xl">
          <p className="mb-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
            Why liver health matters
          </p>
          <h2 className="font-display text-4xl font-medium leading-[1.08] tracking-tight text-sa-forest sm:text-5xl">
            The organ that does everything —
            <span className="text-sa-muted"> and warns you last.</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-sa-muted">
            Your liver works 24/7 — yet most liver diseases don't cause symptoms
            until they're advanced. In India the scale is staggering, and early
            assessment can identify risk long before symptoms ever appear.
          </p>
        </Reveal>

        {/* Bento stats */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-6"
        >
          {/* Combined epidemic mega card — spans full width */}
          <motion.div
            variants={fadeUp}
            className="relative overflow-hidden md:col-span-6 rounded-[28px] bg-sa-forest p-8 text-white md:p-12"
          >
            {/* soft copper glow */}
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-sa-copper/15 blur-3xl" />
            <div className="pointer-events-none absolute -left-16 -bottom-16 h-48 w-48 rounded-full bg-sa-copper/10 blur-3xl" />

            <div className="relative flex items-center gap-3 text-sa-copper">
              <Users className="h-5 w-5" />
              <span className="text-[13px] font-semibold uppercase tracking-widest">
                The silent epidemic
              </span>
            </div>

            <div className="relative mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-16">
              {/* Left — India */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/50">
                  In India
                </p>
                <p className="mt-3 font-display text-6xl font-medium tracking-tight md:text-7xl">
                  ~400M
                </p>
                <p className="mt-3 max-w-md text-white/70">
                  Indians may already have fatty liver — <span className="text-white">more people than the entire population of the United States.</span>
                </p>
              </div>

              {/* Divider — vertical on desktop, horizontal on mobile */}
              <div className="pointer-events-none absolute left-0 right-0 top-1/2 hidden h-px -translate-y-1/2 bg-white/10 md:left-1/2 md:top-0 md:h-full md:w-px md:translate-y-0 md:bg-white/10 md:block" />

              {/* Right — Global */}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/50">
                  Worldwide
                </p>
                <p className="mt-3 font-display text-6xl font-medium tracking-tight md:text-7xl">
                  1 in 3 <span className="text-white/60">adults</span>
                </p>
                <p className="mt-3 max-w-md text-white/70">
                  has fatty liver (MASLD) — affecting <span className="text-white">30-38%</span> of adults globally. Most don't know it, because early disease is silent.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Silent but Serious */}
          <motion.div
            variants={fadeUp}
            className="md:col-span-2 flex flex-col justify-between rounded-[28px] border border-sa-beige bg-white p-8 md:p-10"
          >
            <div>
              <p className="font-display text-4xl font-medium tracking-tight text-sa-forest md:text-5xl">
                Silent but Serious
              </p>
              <p className="mt-3 max-w-md text-sa-muted">
                Early liver disease is often reversible; advanced disease may
                not be. Early assessment identifies risk before symptoms appear.
              </p>
            </div>
            <ShieldAlert className="hidden h-16 w-16 flex-none text-sa-copper/30 sm:block" />
          </motion.div>

          {/* Global mortality — dark */}
          <motion.div
            variants={fadeUp}
            className="md:col-span-4 flex flex-col justify-between rounded-[28px] bg-sa-forest p-8 text-white md:p-10"
          >
            <div className="flex items-center gap-3 text-sa-copper">
              <Globe2 className="h-5 w-5" />
              <span className="text-[13px] font-semibold uppercase tracking-widest">
                Global mortality
              </span>
            </div>
            <div className="mt-8">
              <p className="font-display text-5xl font-medium tracking-tight md:text-6xl">
                2 Million+
              </p>
              <p className="mt-3 max-w-md text-white/70">
                deaths from liver disease every year — <span className="text-white">1 in every 25 deaths</span> worldwide.
              </p>
            </div>
          </motion.div>
        </motion.div>

        {/* Disease progression — visual timeline */}
        <div className="mt-16">
          <Reveal>
            <h3 className="font-display text-2xl font-medium tracking-tight text-sa-forest md:text-3xl">
              Most liver disease develops silently.
            </h3>
            <p className="mt-3 max-w-2xl text-sa-muted">
              A silent progression that can span years — reversible in the
              earliest stages, critical in the last.
            </p>
          </Reveal>

          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-10 rounded-[32px] border border-sa-beige bg-white p-6 md:p-10"
            data-testid="liver-progression"
          >
            {/* Horizontal on md+, vertical stack on mobile */}
            <div className="flex flex-col items-stretch gap-6 md:flex-row md:items-center md:justify-between md:gap-2">
              {STAGES.map((s, i) => (
                <div key={s.name} className="flex flex-col items-center gap-6 md:flex-row md:gap-2">
                  <motion.div
                    variants={fadeUp}
                    className="flex flex-col items-center text-center"
                    data-testid={`liver-stage-${i}`}
                  >
                    <div className="relative flex h-24 w-32 items-center justify-center md:h-28 md:w-36">
                      <img
                        src={s.img}
                        alt={`${s.name} liver`}
                        loading="lazy"
                        className="h-full w-full object-contain drop-shadow-sm"
                      />
                    </div>
                    <span
                      className="mt-3 inline-flex h-2 w-2 rounded-full"
                      style={{ backgroundColor: s.dot }}
                      aria-hidden
                    />
                    <p className="mt-2 font-display text-[13px] font-medium uppercase tracking-[0.14em] text-sa-forest">
                      {s.name}
                    </p>
                    <p className="mt-1 text-[12px] leading-relaxed text-sa-muted">
                      {s.caption}
                    </p>
                  </motion.div>
                  {/* Arrow separator (hidden after last) */}
                  {i < STAGES.length - 1 && (
                    <motion.div
                      variants={fadeUp}
                      className="flex items-center justify-center text-sa-copper/60"
                      aria-hidden
                    >
                      <ArrowRight className="hidden h-6 w-6 md:block" strokeWidth={1.5} />
                      <div className="h-8 w-px bg-sa-copper/30 md:hidden" />
                    </motion.div>
                  )}
                </div>
              ))}
            </div>

            {/* Reversibility strip below the timeline */}
            <div className="mt-8 grid grid-cols-1 gap-3 border-t border-sa-beige pt-6 sm:grid-cols-2">
              <div className="flex items-center gap-3 rounded-2xl bg-[#EFE7DA] px-4 py-3">
                <span className="h-2.5 w-2.5 flex-none rounded-full bg-[#5B8265]" />
                <p className="text-sm text-sa-forest">
                  <span className="font-medium">Reversible zone</span>
                  <span className="text-sa-muted"> — Healthy · Fatty Liver</span>
                </p>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-[#EFE7DA] px-4 py-3">
                <span className="h-2.5 w-2.5 flex-none rounded-full bg-[#A24A34]" />
                <p className="text-sm text-sa-forest">
                  <span className="font-medium">Critical zone</span>
                  <span className="text-sa-muted"> — Fibrosis · Cirrhosis · Liver Cancer</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyLiver;
